import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import API from "../Services/Report";
import APISS from "../Services/Location";
import * as XLSX from "xlsx";
const PER_PAGE = 10;

const AuditReport = () => {
    // Default Dates
    const today = useMemo(() => new Date(), []);
    const formattedToday = today.toISOString().split("T")[0];

    const pastDate = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 90);
        return date;
    }, []);

    const formattedPastDate = pastDate.toISOString().split("T")[0];

    const [auditReport, setAuditReport] = useState([]);
    const [location, setLocation] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [selectedLocation, setSelectedLocation] = useState("");
    const [searchButtonDisabled, setSearchButtonDisabled] = useState(false);
    const [searched, setSearched] = useState(false);

    const [search, setSearch] = useState({
        searchText: "",
        from_date: "",
        to_date: "",
    });

    const isSearch =
        selectedLocation !== "" &&
        search.from_date !== "";

    useEffect(() => {
        bindLocation();
    }, []);

    useEffect(() => {
        if (searched) {
            bindAuditReport(currentPage);
        }
    }, [currentPage]);

    const handleFieldChange = (e, field) => {
        setSearch((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const handleError = (error) => {
        if (!error.response) {
            toast.error("Network error! Please check your internet connection.");
            return;
        }

        if (error.response.status === 403) {
            window.location.replace("/Login");
            return;
        }

        toast.error(
            error.response.data?.message ||
            "Something went wrong. Please try again."
        );
    };

    const bindAuditReport = async (pageNo = currentPage) => {
        try {
            const payload = {
                page_size: PER_PAGE,
                page_number: pageNo,
                start_date: search.from_date || formattedPastDate,
                to_date: null,
                location_id: selectedLocation || null,
            };

            const response = await API.getAuditReport(payload);
            const data = response?.data;
            setAuditReport(data?.result || []);
            setTotalCount(data?.total_count || 0);
            setTotalPages(
                Math.ceil((data?.total_count || 0) / PER_PAGE)
            );
        } catch (error) {
            handleError(error);
        }
    };

    const bindLocation = async () => {
        try {
            const response = await APISS.getLocationList({
                is_active: 1,
                limit: 0,
            });

            setLocation(response?.data?.data || []);
        } catch (error) {
            handleError(error);
        }
    };

    const handleSearch = () => {
        if (!selectedLocation || !search.from_date) {
            return;
        }

        setSearched(true);

        if (currentPage !== 1) {
            setCurrentPage(1);
        }

        bindAuditReport(1);
    };


    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Page Numbers
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 10;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const left = Math.max(1, currentPage - 3);
        const right = Math.min(totalPages, currentPage + 3);

        if (left > 2) {
            pages.push(1, "...");
        } else {
            for (let i = 1; i < left; i++) {
                pages.push(i);
            }
        }

        for (let i = left; i <= right; i++) {
            pages.push(i);
        }

        if (right < totalPages - 1) {
            pages.push("...", totalPages);
        } else {
            for (let i = right + 1; i <= totalPages; i++) {
                pages.push(i);
            }
        }

        return pages;
    };

    const locationOptions = useMemo(() => {
        return location.map((item) => ({
            value: item.id,
            label: item.name,
        }));
    }, [location]);

    const handleLocationChange = (selectedOption) => {
        setSelectedLocation(selectedOption?.value || "");
    };
    const tableHeaders = useMemo(() => {
        if (auditReport.length === 0) return [];

        return Object.keys(auditReport[0]).filter(
            (key) => key !== "total_records"
        );
    }, [auditReport]);

    const formatHeader = (header) => {
        return header
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const downloadCSV = async () => {
        try {
            const payload = {
                page_size: totalCount,
                page_number: 1,
                start_date: search.from_date || formattedPastDate,
                to_date: null,
                location_id: selectedLocation || null,
            };
            const response = await API.getAuditReport(payload);
            //console.log('ddd',response?.data?.result);
            const result = response?.data?.result || [];
            if (!result.length) {
                toast.error("No data found!");
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(result);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Waiver");
            XLSX.writeFile(workbook, "AuditReport.xlsx");
        } catch (error) {
            console.error("Error downloading Excel:", error);
        }
    };


    return (
        <div className="col-lg-12 grid-margin stretch-card">
            <div className="card">
                <div className="card-body">
                    <div className="card shadow-sm border-0 mb-3">

                        <div className="row align-items-end g-2">

                            {/* Center */}
                            <div className="col-lg-4">
                                <label className="form-label fw-semibold mb-1">
                                    Center <span className="text-danger">*</span>
                                </label>

                                <Select
                                    options={locationOptions}
                                    placeholder="Select Center"
                                    isSearchable
                                    value={
                                        locationOptions.find(
                                            (item) => item.value === selectedLocation
                                        ) || null
                                    }
                                    onChange={handleLocationChange}
                                />
                            </div>

                            {/* From Date */}
                            <div className="col-md-3">
                                <label className="form-label fw-semibold mb-1">
                                    From Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={search.from_date}
                                    max={formattedToday}
                                    onChange={(e) => handleFieldChange(e, "from_date")}
                                />
                            </div>

                            {/* Search */}
                            <div className="col-md-2">
                                <button
                                    type="button"
                                    className="btn btn-success w-100"
                                    onClick={handleSearch}
                                    disabled={!isSearch}
                                >
                                    <i className="fa fa-search"></i>
                                </button>
                            </div>

                            {/* Reset */}
                            <div className="col-md-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary w-100"
                                    onClick={() => {
                                        setSelectedLocation("");
                                        setSearch({
                                            searchText: "",
                                            from_date: "",
                                            to_date: "",
                                        });
                                        setAuditReport([]);
                                        setCurrentPage(1);
                                        setTotalPages(1);
                                    }}
                                >
                                    <i className="fa fa-refresh"></i>
                                </button>
                            </div>

                            {/* Export */}
                            {totalCount > 0 && (
                                <div className="col-md-1">
                                    <button
                                        className="btn btn-primary"
                                        onClick={downloadCSV}
                                    >
                                        <i className="fa fa-download"></i>
                                    </button>
                                </div>
                            )}


                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-bordered text-center">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {tableHeaders.map((header) => (
                                        <th key={header}>{formatHeader(header)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {auditReport.length > 0 ? (
                                    auditReport.map((row, index) => (
                                        <tr key={row.customer_order_id || index}>
                                            <td>
                                                {(currentPage - 1) * PER_PAGE + index + 1}
                                            </td>

                                            {tableHeaders.map((header) => {
                                                let value = row[header];

                                                if (
                                                    value === null ||
                                                    value === undefined ||
                                                    value === ""
                                                ) {
                                                    value = "-";
                                                }

                                                if (header === "Rupee Spent") {
                                                    value = `₹ ${value}`;
                                                }

                                                return (
                                                    <td
                                                        key={header}
                                                        className={
                                                            header === "SkyCoin (Credit/Debit)"
                                                                ? row[header] >= 0

                                                                : ""
                                                        }
                                                    >
                                                        {value}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={tableHeaders.length + 1}>
                                            No Records Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <nav className="mt-4">
                            <ul className="pagination justify-content-center">

                                <li
                                    className={`page-item ${currentPage === 1 ? "disabled" : ""
                                        }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() =>
                                            handlePageChange(currentPage - 1)
                                        }
                                    >
                                        Previous
                                    </button>
                                </li>

                                {getPageNumbers().map((page, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${page === currentPage
                                            ? "active"
                                            : ""
                                            } ${page === "..."
                                                ? "disabled"
                                                : ""
                                            }`}
                                    >
                                        {page === "..." ? (
                                            <span className="page-link">
                                                ...
                                            </span>
                                        ) : (
                                            <button
                                                className="page-link"
                                                onClick={() =>
                                                    handlePageChange(page)
                                                }
                                            >
                                                {page}
                                            </button>
                                        )}
                                    </li>
                                ))}

                                <li
                                    className={`page-item ${currentPage === totalPages
                                        ? "disabled"
                                        : ""
                                        }`}
                                >
                                    <button
                                        className="page-link"
                                        onClick={() =>
                                            handlePageChange(currentPage + 1)
                                        }
                                    >
                                        Next
                                    </button>
                                </li>

                            </ul>
                        </nav>
                    )}

                </div>
            </div>

            <ToastContainer />
        </div>
    );
};

export default AuditReport;
